import React, { useEffect } from "react";
import { Controller, ControllerRenderProps, FieldValues, useForm } from "react-hook-form";
import classNames from "classnames";
import { DateTime } from "luxon";
import MaterialTextInput from "@components/UI/Buttons/MaterialTextInput/MaterialTextInput";
import { useGenerateDocMutation } from "@app/store/docsApi";
import DatePickerInput from "@shared/ui/inputs/date-picker-input/date-picker-input";
import { ToggleSwitch } from "@shared/ui/ToggleSwitch";
import {
    ACCEPTANCE_CERT_DATE,
    CAR_BODY_NUMBER,
    CAR_COLOR,
    CAR_ENGINE_NUMBER,
    CAR_ENGINE_OIL,
    CAR_ENGINE_POWER,
    CAR_ENGINE_VOLUME,
    CAR_FRAME_NUMBER,
    CAR_MILEAGE,
    CAR_MODEL,
    CAR_NUMBER,
    CAR_PRICE,
    CAR_TYPE,
    CAR_VIN,
    CAR_YEAR,
    CREATION_DATE,
    CREATION_PLACE,
    CUSTOMER_FIO,
    CUSTOMER_PASSPORT_ADDRESS,
    CUSTOMER_PASSPORT_BIRTHDAY,
    CUSTOMER_PASSPORT_CODE,
    CUSTOMER_PASSPORT_DATE,
    CUSTOMER_PASSPORT_ISSUER,
    CUSTOMER_PASSPORT_NUMBER,
    CUSTOMER_PASSPORT_SERIES,
    DAMAGE_DESCRIPTION,
    FIO,
    IS_SELLER,
    PASSPORT_ADDRESS,
    PASSPORT_BIRTHDAY,
    PASSPORT_CODE,
    PASSPORT_DATE,
    PASSPORT_ISSUER,
    PASSPORT_NUMBER,
    PASSPORT_SERIES,
    PTC_DATE,
    PTC_ISSUER,
    PTC_SERIES,
    PTS_NUMBER,
    STS_DATE,
    STS_ISSUER,
    STS_NUMBER,
    STS_SERIES
} from "./contract-generator.constants";
import { MAX_LENGTH_INPUTS, Validations } from "./contract-generator-form.validations";
import classes from "./contract-generator-form.module.scss";

const ContractGeneratorForm = () => {
    const [generateDoc, generateDocState] = useGenerateDocMutation();

    const [agreement, setAgreement] = React.useState(false);
    const toggleAgreement = () => {
        setAgreement((prev) => !prev);
    };

    const {
        handleSubmit,
        formState: { errors },
        control,
        watch
    } = useForm({
        mode: "all"
    });

    const displayError = (name: string) => {
        return (
            <>
                {errors?.[name] && (
                    <span className={classes.Error}>
                        {errors?.[name]?.message && errors?.[name]?.message?.toString()}
                    </span>
                )}
            </>
        );
    };

    function fieldChangeHandler<T extends string>(field: ControllerRenderProps<FieldValues, T>) {
        return (val: string | Date | null) => {
            if (val instanceof Date) {
                const date = DateTime.fromJSDate(val);
                const formatted = date.toFormat("dd.MM.yyyy").toString();
                field.onChange(formatted);
            } else {
                field.onChange(val);
            }
        };
    }

    const handlePassportCodeInput = (e: React.FormEvent<HTMLInputElement>) => {
        let value = e.currentTarget.value.replace(/\D/g, "");
        if (value.length > 3) {
            value = `${value.slice(0, 3)}-${value.slice(3, 6)}`;
        }
        e.currentTarget.value = value;
    };

    const handleUpperCaseInput = (e: React.FormEvent<HTMLInputElement>) => {
        const input = e.target as HTMLInputElement;
        input.value = input.value.toUpperCase();
    };

    const onFormSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        const func = handleSubmit(async (data) => {
            await generateDoc({
                ...data,
                disableValidation: true
            });
        });

        func();
    };

    const getValidations = (name: keyof typeof Validations) => {
        return Validations[name];
    };

    const [hasEmptyFields, setHasEmptyFields] = React.useState(true);

    const confirmButtonDisabled = hasEmptyFields
        ? !agreement || generateDocState.isLoading
        : generateDocState.isLoading;

    useEffect(() => {
        const { unsubscribe } = watch((value) => {
            const hasEmptyFields = Object.keys(value).some((key) => {
                return !value[key];
            });

            setHasEmptyFields(hasEmptyFields);
        });
        return () => unsubscribe();
    }, [watch]);

    return (
        <div className={classes.FormScreen}>
            <h1>
                Договор купли-продажи автомобиля,
                <br /> мотоцикла, транспортного средства
            </h1>

            <div
                style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                    flexWrap: "wrap"
                }}
            >
                <div className={classes.FormGroup}>
                    <label>Вы покупаете автомобиль?</label>
                    <Controller
                        control={control}
                        name={IS_SELLER}
                        defaultValue={true}
                        render={({ field }) => {
                            return (
                                <div className={classes.ButtonsLine}>
                                    <button
                                        onClick={() => {
                                            field.onChange(true);
                                        }}
                                        className={classNames({
                                            [classes.PrimaryButton]: field.value,
                                            [classes.Button]: !field.value
                                        })}
                                    >
                                        Да, покупаю
                                    </button>
                                    <button
                                        onClick={() => {
                                            field.onChange(false);
                                        }}
                                        className={classNames({
                                            [classes.PrimaryButton]: !field.value,
                                            [classes.Button]: field.value
                                        })}
                                    >
                                        Нет, продаю
                                    </button>
                                </div>
                            );
                        }}
                    />
                </div>

                <div className={classes.FormGroup}>
                    <label>Дата и место составления</label>
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CREATION_DATE}
                        rules={getValidations(CREATION_DATE)}
                        render={({ field }) => {
                            return (
                                <>
                                    <DatePickerInput
                                        label="Дата составления"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CREATION_DATE)}
                                </>
                            );
                        }}
                    />
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CREATION_PLACE}
                        rules={getValidations(CREATION_PLACE)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Место составления"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CREATION_PLACE)}
                                </>
                            );
                        }}
                    />

                    <Controller
                        control={control}
                        defaultValue={""}
                        name={ACCEPTANCE_CERT_DATE}
                        rules={getValidations(ACCEPTANCE_CERT_DATE)}
                        render={({ field }) => {
                            return (
                                <>
                                    <DatePickerInput
                                        label="Дата подписания акта приема-передачи"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(ACCEPTANCE_CERT_DATE)}
                                </>
                            );
                        }}
                    />
                </div>

                <div className={classes.FormGroup}>
                    <label>Текущий владелец транспортного средства</label>
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={FIO}
                        rules={getValidations(FIO)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="ФИО"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(FIO)}
                                </>
                            );
                        }}
                    />

                    <div className={classes.Line}>
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={PASSPORT_SERIES}
                            rules={getValidations(PASSPORT_SERIES)}
                            render={({ field }) => {
                                return (
                                    <>
                                        <div>
                                            <MaterialTextInput
                                                type="text"
                                                label="Серия паспорта"
                                                value={field.value}
                                                maxLength={MAX_LENGTH_INPUTS.PASSPORT_SERIES}
                                                onChange={fieldChangeHandler(field)}
                                            />
                                            {displayError(PASSPORT_SERIES)}
                                        </div>
                                    </>
                                );
                            }}
                        />
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={PASSPORT_NUMBER}
                            rules={getValidations(PASSPORT_NUMBER)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="Номер паспорта"
                                            value={field.value}
                                            maxLength={MAX_LENGTH_INPUTS.PASSPORT_NUMBER}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(PASSPORT_NUMBER)}
                                    </div>
                                );
                            }}
                        />
                    </div>

                    <div className={classes.Line}>
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={PASSPORT_DATE}
                            rules={getValidations(PASSPORT_DATE)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <DatePickerInput
                                            label="Дата выдачи"
                                            value={field.value}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(PASSPORT_DATE)}
                                    </div>
                                );
                            }}
                        />
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={PASSPORT_CODE}
                            rules={getValidations(PASSPORT_CODE)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="Код подразделения"
                                            value={field.value}
                                            maxLength={MAX_LENGTH_INPUTS.PASSPORT_CODE}
                                            onInput={handlePassportCodeInput}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(PASSPORT_CODE)}
                                    </div>
                                );
                            }}
                        />
                    </div>
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={PASSPORT_ISSUER}
                        rules={getValidations(PASSPORT_ISSUER)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Кем выдан"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(PASSPORT_ISSUER)}
                                </>
                            );
                        }}
                    />
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={PASSPORT_BIRTHDAY}
                        rules={getValidations(PASSPORT_BIRTHDAY)}
                        render={({ field }) => {
                            return (
                                <>
                                    <DatePickerInput
                                        label="Дата рождения"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(PASSPORT_BIRTHDAY)}
                                </>
                            );
                        }}
                    />
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={PASSPORT_ADDRESS}
                        rules={getValidations(PASSPORT_ADDRESS)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Адрес регистрации"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(PASSPORT_ADDRESS)}
                                </>
                            );
                        }}
                    />
                </div>

                <div className={classes.FormGroup}>
                    <label>Покупатель транспортного средства</label>
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CUSTOMER_FIO}
                        rules={getValidations(CUSTOMER_FIO)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="ФИО"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CUSTOMER_FIO)}
                                </>
                            );
                        }}
                    />
                    <div className={classes.Line}>
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={CUSTOMER_PASSPORT_SERIES}
                            rules={getValidations(CUSTOMER_PASSPORT_SERIES)}
                            render={({ field }) => {
                                return (
                                    <>
                                        <div>
                                            <MaterialTextInput
                                                type="text"
                                                label="Серия паспорта"
                                                value={field.value}
                                                maxLength={MAX_LENGTH_INPUTS.PASSPORT_SERIES}
                                                onChange={fieldChangeHandler(field)}
                                            />

                                            {displayError(CUSTOMER_PASSPORT_SERIES)}
                                        </div>
                                    </>
                                );
                            }}
                        />
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={CUSTOMER_PASSPORT_NUMBER}
                            rules={getValidations(CUSTOMER_PASSPORT_NUMBER)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="Номер паспорта"
                                            value={field.value}
                                            maxLength={MAX_LENGTH_INPUTS.PASSPORT_NUMBER}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(CUSTOMER_PASSPORT_NUMBER)}
                                    </div>
                                );
                            }}
                        />
                    </div>
                    <div className={classes.Line}>
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={CUSTOMER_PASSPORT_DATE}
                            rules={getValidations(CUSTOMER_PASSPORT_DATE)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <DatePickerInput
                                            label="Дата выдачи"
                                            value={field.value}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(CUSTOMER_PASSPORT_DATE)}
                                    </div>
                                );
                            }}
                        />
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={CUSTOMER_PASSPORT_CODE}
                            rules={getValidations(CUSTOMER_PASSPORT_CODE)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="Код подразделения"
                                            value={field.value}
                                            maxLength={MAX_LENGTH_INPUTS.PASSPORT_CODE}
                                            onInput={handlePassportCodeInput}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(CUSTOMER_PASSPORT_CODE)}
                                    </div>
                                );
                            }}
                        />
                    </div>
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CUSTOMER_PASSPORT_ISSUER}
                        rules={getValidations(CUSTOMER_PASSPORT_ISSUER)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Кем выдан"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CUSTOMER_PASSPORT_ISSUER)}
                                </>
                            );
                        }}
                    />
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CUSTOMER_PASSPORT_BIRTHDAY}
                        rules={getValidations(CUSTOMER_PASSPORT_BIRTHDAY)}
                        render={({ field }) => {
                            return (
                                <>
                                    <DatePickerInput
                                        label="Дата рождения"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CUSTOMER_PASSPORT_BIRTHDAY)}
                                </>
                            );
                        }}
                    />
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CUSTOMER_PASSPORT_ADDRESS}
                        rules={getValidations(CUSTOMER_PASSPORT_ADDRESS)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Адрес регистрации"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CUSTOMER_PASSPORT_ADDRESS)}
                                </>
                            );
                        }}
                    />
                </div>

                <div className={classes.FormGroup}>
                    <label>Транспортное средство</label>
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CAR_NUMBER}
                        rules={getValidations(CAR_NUMBER)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Гос. номер"
                                        value={field.value}
                                        onInput={handleUpperCaseInput}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CAR_NUMBER)}
                                </>
                            );
                        }}
                    />

                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CAR_VIN}
                        rules={getValidations(CAR_VIN)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="VIN номер"
                                        value={field.value}
                                        maxLength={MAX_LENGTH_INPUTS.CAR_VIN}
                                        onInput={handleUpperCaseInput}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CAR_VIN)}
                                </>
                            );
                        }}
                    />

                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CAR_MODEL}
                        rules={getValidations(CAR_MODEL)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Марка и модель ТС"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CAR_MODEL)}
                                </>
                            );
                        }}
                    />

                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CAR_TYPE}
                        rules={getValidations(CAR_TYPE)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Тип ТС (легковой, грузовой, автобус, мотоцикл)"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CAR_TYPE)}
                                </>
                            );
                        }}
                    />

                    <div className={classes.Line}>
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={CAR_YEAR}
                            rules={getValidations(CAR_YEAR)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="Год выпуска"
                                            value={field.value}
                                            maxLength={MAX_LENGTH_INPUTS.CAR_YEAR}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(CAR_YEAR)}
                                    </div>
                                );
                            }}
                        />
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={CAR_MILEAGE}
                            rules={getValidations(CAR_MILEAGE)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="Пробег, км"
                                            value={field.value}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(CAR_MILEAGE)}
                                    </div>
                                );
                            }}
                        />
                    </div>
                    <div className={classes.Line}>
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={CAR_ENGINE_POWER}
                            rules={getValidations(CAR_ENGINE_POWER)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="Мощность двигателя, л.с."
                                            value={field.value}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(CAR_ENGINE_POWER)}
                                    </div>
                                );
                            }}
                        />
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={CAR_ENGINE_VOLUME}
                            rules={getValidations(CAR_ENGINE_VOLUME)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="Рабочий объем, куб.см."
                                            value={field.value}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(CAR_ENGINE_VOLUME)}
                                    </div>
                                );
                            }}
                        />
                    </div>
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CAR_COLOR}
                        rules={getValidations(CAR_COLOR)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Цвет"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CAR_COLOR)}
                                </>
                            );
                        }}
                    />

                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CAR_ENGINE_NUMBER}
                        rules={getValidations(CAR_ENGINE_NUMBER)}
                        render={({ field }) => {
                            return (
                                <div>
                                    <MaterialTextInput
                                        type="text"
                                        label="Номер двигателя"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CAR_ENGINE_NUMBER)}
                                </div>
                            );
                        }}
                    />
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CAR_FRAME_NUMBER}
                        rules={getValidations(CAR_FRAME_NUMBER)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Номер шасси, рамы"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CAR_FRAME_NUMBER)}
                                </>
                            );
                        }}
                    />
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CAR_BODY_NUMBER}
                        rules={getValidations(CAR_BODY_NUMBER)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Номер кузова"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CAR_BODY_NUMBER)}
                                </>
                            );
                        }}
                    />
                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CAR_ENGINE_OIL}
                        rules={getValidations(CAR_ENGINE_OIL)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Информация о масле в двигателе"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(CAR_ENGINE_OIL)}
                                </>
                            );
                        }}
                    />

                    <Controller
                        control={control}
                        defaultValue={""}
                        name={DAMAGE_DESCRIPTION}
                        rules={getValidations(DAMAGE_DESCRIPTION)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="Описание повреждений и дефектов"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(DAMAGE_DESCRIPTION)}
                                </>
                            );
                        }}
                    />

                    <div className={classes.Line}>
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={PTC_SERIES}
                            rules={getValidations(PTC_SERIES)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="ПТС, серия"
                                            value={field.value}
                                            maxLength={MAX_LENGTH_INPUTS.PTC_SERIES}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(PTC_SERIES)}
                                    </div>
                                );
                            }}
                        />
                        <Controller
                            control={control}
                            defaultValue={""}
                            name={PTS_NUMBER}
                            rules={getValidations(PTS_NUMBER)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="ПТС, номер"
                                            value={field.value}
                                            maxLength={MAX_LENGTH_INPUTS.PTS_NUMBER}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(PTS_NUMBER)}
                                    </div>
                                );
                            }}
                        />

                        <Controller
                            control={control}
                            defaultValue={""}
                            name={PTC_DATE}
                            rules={getValidations(PTC_DATE)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <DatePickerInput
                                            label="ПТС, дата выдачи"
                                            value={field.value}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(PTC_DATE)}
                                    </div>
                                );
                            }}
                        />
                    </div>

                    <Controller
                        control={control}
                        defaultValue={""}
                        rules={getValidations(PTC_ISSUER)}
                        name={PTC_ISSUER}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="ПТС, кем выдан"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(PTC_ISSUER)}
                                </>
                            );
                        }}
                    />

                    <div className={classes.Line}>
                        <Controller
                            name={STS_SERIES}
                            control={control}
                            defaultValue={""}
                            rules={getValidations(STS_SERIES)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="СТС, серия"
                                            value={field.value}
                                            maxLength={MAX_LENGTH_INPUTS.STS_SERIES}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(STS_SERIES)}
                                    </div>
                                );
                            }}
                        />
                        <Controller
                            name={STS_NUMBER}
                            control={control}
                            defaultValue={""}
                            rules={getValidations(STS_NUMBER)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <MaterialTextInput
                                            type="text"
                                            label="СТС, номер"
                                            value={field.value}
                                            maxLength={MAX_LENGTH_INPUTS.STS_NUMBER}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(STS_NUMBER)}
                                    </div>
                                );
                            }}
                        />

                        <Controller
                            control={control}
                            defaultValue={""}
                            name={STS_DATE}
                            rules={getValidations(STS_DATE)}
                            render={({ field }) => {
                                return (
                                    <div>
                                        <DatePickerInput
                                            label="СТС, Дата выдачи"
                                            value={field.value}
                                            onChange={fieldChangeHandler(field)}
                                        />
                                        {displayError(STS_DATE)}
                                    </div>
                                );
                            }}
                        />
                    </div>

                    <Controller
                        control={control}
                        defaultValue={""}
                        name={STS_ISSUER}
                        rules={getValidations(STS_ISSUER)}
                        render={({ field }) => {
                            return (
                                <>
                                    <MaterialTextInput
                                        type="text"
                                        label="СТС, кем выдан"
                                        value={field.value}
                                        onChange={fieldChangeHandler(field)}
                                    />
                                    {displayError(STS_ISSUER)}
                                </>
                            );
                        }}
                    />

                    <Controller
                        control={control}
                        defaultValue={""}
                        name={CAR_PRICE}
                        rules={getValidations(CAR_PRICE)}
                        render={({ field }) => {
                            return (
                                <MaterialTextInput
                                    type="number"
                                    label="Стоимость ТС, ₽"
                                    postfix="₽"
                                    value={field.value}
                                    onChange={fieldChangeHandler(field)}
                                />
                            );
                        }}
                    />
                </div>

                {hasEmptyFields && (
                    <div className={classes.FormGroup}>
                        <div>
                            <ToggleSwitch
                                label="Я знаю что заполнил не все поля, при скачивании документа остальные
                        параметры заполнять буду собственноручно."
                                onChange={toggleAgreement}
                                checked={agreement}
                            />
                        </div>
                    </div>
                )}

                <button
                    onClick={onFormSubmit}
                    className={classNames(classes.EstimateButton, {
                        [classes.Disabled]: confirmButtonDisabled,
                        [classes.IsLoading]: generateDocState.isLoading
                    })}
                    disabled={confirmButtonDisabled}
                >
                    Скачать <div className={classes.Loader}></div>
                </button>
            </div>
        </div>
    );
};
export default ContractGeneratorForm;